import _ from 'underscore';
import { useHistory } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { CheckCircle, Search, X } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';

import Paper from 'components/paper';
import Select from 'components/select';
import List from 'components/list/list';
import TextField from 'components/text-field';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';
import Breadcrumbs from 'components/breadcrumbs';
import ListCell from 'components/list/list-cell';
import FilterSidebar from 'components/filter-sidebar';
import RadioSelectItem from 'components/radio-select-item';
import CheckboxSelectItem from 'components/checkbox-select-item';
import ProfilesStrategiesTabHeader from '../../components/profile-strategies-tabs-header';

import { useGet } from 'hooks/use-get';
import { usePagination } from 'hooks/use-pagination';
import { OwnerType } from 'types/shared/owner-type.enum';
import { getFeedStrategies } from './api/get-feed-strategies';
import { useAccountState } from 'contexts/account/account-context';
import { FeedStrategiesListItem } from './types/feed-strategies-list-item.type';

type Props = {};

const PAGINATION_LIMIT = 100;
const PAGE_NAME_ID = 'profilesStrategies.listFeedStrategies';

const ListFeedStrategies: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const { account } = useAccountState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // #region STATE
  const [searchValue, setSearchValue] = useState('');
  const [ownerType, setOwnerType] = useState<OwnerType>(OwnerType.MARKET);
  const [rows, setRows] = useState<FeedStrategiesListItem[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['active']);
  const [siteId, setSiteId] = useState(_.first(account.company!.addressAccess.filter((site) => site.seaCloudId))?.id);
  // #endregion STATE

  const getOwnerTypeId = () => {
    switch (ownerType) {
      case OwnerType.MARKET:
        return account.company?.marketId;
      case OwnerType.COMPANY:
        return account.company?.companyId;
      case OwnerType.SITE:
        return siteId;
    }
  };

  // #region FETCH DATA
  const { data: feedStrategies, isLoading } = useGet(
    ['getFeedStrategies', ownerType, String(getOwnerTypeId())],
    (context) => getFeedStrategies(context.queryKey[1] as OwnerType, String(context.queryKey[2])),
    {
      enabled: !!ownerType,
    }
  );

  useEffect(() => {
    const search = searchValue.toLowerCase();
    setRows((feedStrategies ?? [])
      .filter((item) =>
        selectedFilters.includes('active') === item.active &&
        selectedFilters.includes('isTemporary') === item.isTemporary &&
        (!search || item.name.toLowerCase().includes(search))
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    );
  }, [feedStrategies, selectedFilters, searchValue]);
  // #endregion FETCH DATA

  // #region HANDLERS
  const handleOnClick = (url: string) => {
    history.push(url);
  };
  // #endregion HANDLERS

  return (
    <>
      <ProfilesStrategiesTabHeader utils={<Breadcrumbs />} />
      <Paper transparent>
        <Box
          flexDirection={isMobile ? 'column-reverse' : 'row'}
          display={'flex'}
          flexWrap={'wrap'}
          gap={4}
          justifyContent={'flex-end'}
          pb={4}>
          <Box display={'flex'} flexWrap={'wrap'} gap={4}>
            <Button onClick={() => handleOnClick(`/profiles-strategies/feed-strategies/create`)}>
              <FormattedMessage
                id={`${PAGE_NAME_ID}.createButton`}
                values={{ type: intl.formatMessage({ id: `profilesStrategies.feedStrategy` }) }}
              />
            </Button>
          </Box>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <Box
            gap={4}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-end'}
            flexDirection={isMobile ? 'column-reverse' : 'row'}>
            <FilterSidebar>
              {ownerType === OwnerType.SITE && (
                <Box pb={isMobile ? 4 : 0}>
                  <Select
                    fullWidth={isMobile}
                    label={intl.formatMessage({ id: `general.site` })}
                    margin="none"
                    value={siteId}
                    onChange={(event) => setSiteId(String(event.target.value))}>
                    {_.chain(account.company?.addressAccess)
                      .sortBy((s) => s.name.toLowerCase())
                      .value()
                      .map((site) => (
                        <RadioSelectItem key={site.id} value={site.id} selected={siteId === site.id}>
                          {site.name}
                        </RadioSelectItem>
                      ))}
                  </Select>
                </Box>
              )}
              <Select
                label={intl.formatMessage({ id: `profilesStrategies.ownerTitle` })}
                margin="none"
                value={ownerType}
                onChange={(event) => setOwnerType(event.target.value as OwnerType)}>
                <RadioSelectItem value={OwnerType.MARKET} selected={OwnerType.MARKET === ownerType}>
                  <FormattedMessage id={`profilesStrategies.menuItem.market`} />
                </RadioSelectItem>
                <RadioSelectItem value={OwnerType.COMPANY} selected={OwnerType.COMPANY === ownerType}>
                  <FormattedMessage id={`profilesStrategies.menuItem.company`} />
                </RadioSelectItem>
                <RadioSelectItem value={OwnerType.SITE} selected={OwnerType.SITE === ownerType}>
                  <FormattedMessage id={`profilesStrategies.menuItem.site`} />
                </RadioSelectItem>
              </Select>
              <Select
                multiple
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.state.title` })}
                value={selectedFilters}
                onChange={(event) => setSelectedFilters(event.target.value as string[])}>
                <CheckboxSelectItem checked={selectedFilters.includes('active')} value={'active'}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.filters.state.active`} />
                </CheckboxSelectItem>
                <CheckboxSelectItem checked={selectedFilters.includes('isTemporary')} value={'isTemporary'}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.filters.state.isTemporary`} />
                </CheckboxSelectItem>
              </Select>
              <TextField
                label={intl.formatMessage({ id: 'general.search' })}
                value={searchValue}
                endAdornment={
                  searchValue ? <X size={18} style={{ cursor: 'pointer' }} onClick={() => setSearchValue('')} /> : <Search size={18} />
                }
                onChange={(event) => {
                  setSearchValue(event.target.value);
                }}
              />
            </FilterSidebar>
          </Box>
          <List
            options={[
              {
                id: 'name',
                label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }),
                comparator: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
              },
              {
                id: 'numberOfConnectedFishgroups',
                label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.numberOfConnectedFishGroupsLabel` }),
                align: 'center',
              },
              { id: 'active', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.activeLabel` }), align: 'center' },
              { id: 'isTemporary', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.isTemporaryLabel` }), align: 'center' },
              { id: 'actions', label: '', align: 'right', hide: !isMobile, disableSort: true },
            ]}
            rows={rows}
            sort={setRows}
            isLoading={isLoading}
            noResult={{
              show: rows.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {rows.slice(from, to).map((row, idx) => (
              <ListRow key={idx} onClick={() => handleOnClick(`/profiles-strategies/feed-strategies/${row.id}`)}>
                <ListCell align="left">{row.name}</ListCell>
                <ListCell align="center">{row.numberOfConnectedFishgroups}</ListCell>
                <ListCell align={isMobile ? 'right' : 'center'}>
                  <Box>{row.active && <CheckCircle color={theme.palette.darkBlue.main} size={22} />}</Box>
                </ListCell>
                <ListCell align={isMobile ? 'right' : 'center'}>
                  <Box>{row.isTemporary && <CheckCircle color={theme.palette.darkBlue.main} size={22} />}</Box>
                </ListCell>
                <ListCell align="right" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    fullWidth={isMobile}
                    size={'small'}
                    onClick={() => handleOnClick(`/profiles-strategies/feed-strategies/${row.id}`)}>
                    <FormattedMessage id="general.view" />
                  </Button>
                </ListCell>
              </ListRow>
            ))}
          </List>
          <Pagination currentPage={currentPage} pages={Math.ceil(rows.length / PAGINATION_LIMIT)} onClick={handlePagination} />
        </Box>
      </Paper>
    </>
  );
};

export default ListFeedStrategies;
